<script setup>
import { inject, computed, onBeforeMount } from 'vue';
import ProductCardButton from '@/app/components/product-card/product-card-button.vue';
import ProductCardDetailRange from '@/app/components/product-card/product-card-detail-range.vue';
import ProductCardDetailNonRange from '@/app/components/product-card/product-card-detail-non-range.vue';
import ProductCardTabs from '@/app/components/product-card/product-card-tabs.vue';
import ProductCardTabContent from '@/app/components/product-card/product-card-tab-content.vue';

import { useProductsStore } from '@/app/stores/productsStore.js';
const productsStore = useProductsStore();

const config = inject('config');
const gaPush = inject('gaPush');
const props = defineProps({
  product: {
    type: Object,
    required: true,
  },
  position: {
    type: Number,
    required: false,
  },
  showDetails: {
    type: Boolean,
    default: true,
  },
  showTags: {
    type: Boolean,
    default: true,
  },
  standalone: {
    type: Boolean,
    default: false,
  },
  productTitleHeadingLevel: {
    type: Number,
    default: 2,
  },
  gaPrefix: {
    type: String,
    default: 'prod_spec_',
  },
  productDetailsHeading: {
    type: String,
    required: false,
  },
});

const componentTag = computed(() => (props.standalone ? 'div' : 'li'));
const componentAttributes = computed(() => (props.standalone ? {} : { role: 'listitem' }));
const productTitleHeadingTag = computed(() => 'h' + props.productTitleHeadingLevel);
const productDetailsHeadingTag = computed(() => 'h' + (props.productTitleHeadingLevel + 1));

const multipleFinancialOptions = computed(
  () => props.product.finance && props.product.finance.length > 1,
);

const quoteLink = computed(() => {
  return props.product.buttons.filter((button) => button.type === 'price')[0]?.dataUrl;
});
const detailsData = computed(() => productsStore.detailsData(props.product.productData));
const hasFinancialData = computed(() => props.product.finance?.length);
const tags = computed(() =>
  props.product.productData?.filter((data) => data.tag && data.value === 'true'),
);

function hasRangeDetail(data) {
  const system = productsStore.system;

  // data object has either 'value' or 'metricalValue' & 'imperialValue' keys, the same applies for 'unit'
  return (
    (`${system}ValueRange` in data && data[`${system}ValueRange`]) ||
    ('valueRange' in data && data['valueRange'])
  );
}

function handleGaViewMore() {
  const gaPayload = {
    event: props.gaPrefix + 'view_model',
    link_text: config.viewMoreLabel,
    link_url: props.product.viewModelBtnUrl,
    product_name: props.product.title,
    product_category: props.product.category,
    product_flag1: tags.value.some((tag) => tag.type === 'popular'),
    product_flag2: tags.value.some((tag) => tag.type === 'new'),
    measurement_system: productsStore.system || null,
  };
  gaPush(gaPayload);
}

onBeforeMount(() => {
  if (productsStore.locales) {
    return;
  }
  productsStore.setLocales();
  productsStore.system = config.defaultSystem;
});
</script>

<template>
  <component
    :is="componentTag"
    class="c-product-card"
    itemprop="itemListElement"
    itemscope
    itemtype="https://schema.org/Product"
    v-bind="componentAttributes"
  >
    <meta itemprop="position" :content="position" />
    <span itemprop="brand" itemscope itemtype="https://schema.org/Brand">
      <meta itemprop="name" content="JCB" />
    </span>
    <div
      class="c-product-card__main"
      :class="{ 'c-product-card__main--no-finance': !hasFinancialData }"
    >
      <div class="c-product-card__base-info">
        <component :is="productTitleHeadingTag" class="c-product-card__heading" itemprop="name">{{
          product.title
        }}</component>
        <ul v-if="showTags" class="c-product-card__indicators" role="list">
          <li v-for="(tag, i) in tags" role="listitem" :key="`${tag.type}-${i}`">
            <span class="c-product-card__indicator">{{ tag.label }}</span>
          </li>
        </ul>
        <p class="c-product-card__description" itemprop="description">{{ product.description }}</p>
      </div>
      <div class="c-product-card__image">
        <img
          v-if="product.image.altName"
          :src="product.image.url"
          :alt="product.image.altName"
          width="375"
          height="375"
          loading="lazy"
          itemprop="image"
        />
        <img
          v-else
          :src="product.image.url"
          role="presentation"
          width="375"
          height="375"
          loading="lazy"
          itemprop="image"
        />
      </div>
      <div
        class="c-product-card__finance"
        v-if="hasFinancialData"
        itemprop="offers"
        itemscope
        itemtype="https://schema.org/Offer"
      >
        <ProductCardTabs
          v-if="multipleFinancialOptions"
          :tabs="product.finance"
          :quoteLink="quoteLink"
          :productName="product.title"
          :tags="tags"
          :gaPrefix="gaPrefix"
          :category="product.category"
        />
        <ProductCardTabContent
          v-else
          :tab="product.finance[0]"
          single="true"
          :quoteLink="quoteLink"
        />
      </div>
      <div class="c-product-card__actions">
        <ProductCardButton
          v-for="button in product.buttons"
          :key="button.id"
          :button="button"
          :productName="product.title"
          :tags="tags"
          :gaPrefix="gaPrefix"
          :category="product.category"
        />
      </div>
      <a
        @click="handleGaViewMore"
        :href="product.viewModelBtnUrl"
        class="c-product-card__view-model"
        v-if="product.viewModelBtnUrl"
        itemprop="url"
        >{{ config.viewMoreLabel }}</a
      >
      <div v-if="showDetails && detailsData.length" class="c-product-card__details">
        <component
          :is="productDetailsHeadingTag"
          v-if="productDetailsHeading"
          class="c-product-card__details-heading"
        >
          {{ productDetailsHeading }}
        </component>
        <dl class="c-product-card__details-list">
          <template v-for="dataPoint in detailsData" :key="dataPoint.propertyId">
            <ProductCardDetailRange v-if="hasRangeDetail(dataPoint)" :dataPoint="dataPoint" />
            <ProductCardDetailNonRange v-else :dataPoint="dataPoint" />
          </template>
        </dl>
      </div>
    </div>
  </component>
</template>
